import React, { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FileText, Image as ImageIcon, Video, Music, X } from 'lucide-react';
import { setSendMediaFile, setSendMediaFileName } from '../../../store/slices/sendMessageSlice';
import { setMediaFile, setMediaError } from '../../../store/mediaSlice';
import { Badge } from '../../tailwind/badge';
import { Label } from '../../tailwind/label';
import { MEDIA_CONFIGS } from '../../../utils/constants/mediaConstants';

export const SendTemplateMediaUploader = ({ 
  headerType = 'image', 
  disabled = false, 
  mode = 'send',
  onFileChange,
  selectedFile
}) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);

  const { sendMediaFileName } = useSelector((state) => state.sendMessage);
  const { mediaFile, error: mediaError } = useSelector((state) => state.media);
  
  const isCreateMode = mode === 'create';
  const isChatMode = mode === 'chat';
  
  const mediaFileName = isCreateMode ? mediaFile?.file?.name : sendMediaFileName;
  const error = isCreateMode ? mediaError : null;

  const hasFile = isChatMode ? (selectedFile && selectedFile.file) : (isCreateMode ? (mediaFile && mediaFile.file) : mediaFileName);
  const displayFileName = isChatMode ? selectedFile?.file?.name : (isCreateMode ? mediaFile?.file?.name : mediaFileName);
  const displayFileSize = isChatMode ? selectedFile?.file?.size : (isCreateMode ? mediaFile?.file?.size : null);

  // Map lowercase headerType to uppercase MEDIA_TYPE
  const getMediaConfig = (type) => {
    const typeMap = {
      image: MEDIA_CONFIGS.IMAGE,
      video: MEDIA_CONFIGS.VIDEO,
      document: MEDIA_CONFIGS.DOCUMENT,
      audio: MEDIA_CONFIGS.AUDIO,
    };
    const config = typeMap[type?.toLowerCase()];
    if (!config) return null;
    
    // Convert array format to string format for backward compatibility
    return {
      ...config,
      acceptedFormats: config.acceptedFormats.join(','),
    };
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const config = getMediaConfig(headerType);
    if (!config) return;

    const acceptedTypes = config.acceptedFormats.split(',');
    if (!acceptedTypes.some(type => file.type === type.trim())) {
      const errorMsg = `Invalid file type. Expected ${config.label}`;
      switch (mode) {
        case 'create':
          dispatch(setMediaError(errorMsg));
          break;
        case 'chat':
          // Optionally handle chat mode errors if needed
          alert(errorMsg);
          break;
        default:
          alert(errorMsg);
          break;
      }
      return;
    }

    if (file.size > config.maxFileSize) {
      const errorMsg = `File size exceeds ${config.maxSizeText} limit`;
      switch (mode) {
        case 'create':
          dispatch(setMediaError(errorMsg));
          break;
        case 'chat':
          // Optionally handle chat mode errors if needed
          alert(errorMsg);
          break;
        default:
          alert(errorMsg);
          break;
      }
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const filePayload = {
        file: {
          name: file.name,
          type: file.type,
          file: file,
        },
        preview: reader.result,
      };

      switch (mode) {
        case 'chat':
          if (onFileChange) onFileChange(filePayload);
          break;
        case 'create':
          dispatch(setMediaFile(filePayload));
          break;
        default:
          dispatch(setSendMediaFile(filePayload));
          dispatch(setSendMediaFileName(file.name));
          break;
      }
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveFile = () => {
    switch (mode) {
      case 'chat':
        if (onFileChange) onFileChange({ file: null, preview: '' });
        break;
      case 'create':
        dispatch(setMediaFile({ file: null, preview: '' }));
        break;
      default:
        dispatch(setSendMediaFile({ file: null, preview: '' }));
        dispatch(setSendMediaFileName(null));
        break;
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getMediaIcon = () => {
    switch (headerType) {
      case 'image':
        return <ImageIcon className="h-4 w-4 text-gray-600" />;
      case 'video':
        return <Video className="h-4 w-4 text-gray-600" />;
      case 'document':
        return <FileText className="h-4 w-4 text-gray-600" />;
      case 'audio':
        return <Music className="h-4 w-4 text-gray-600" />;
      default:
        return <FileText className="h-4 w-4 text-gray-600" />;
    }
  };

  const getAcceptedFormats = () => {
    const config = getMediaConfig(headerType);
    return config ? config.extensions : '';
  };

  const config = getMediaConfig(headerType);

  return (
    <div className="mt-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-900">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-gray-50 dark:bg-slate-950 border border-gray-200 dark:border-gray-700 flex items-center justify-center">
            {getMediaIcon()}
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900 dark:text-white">Header media</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {isCreateMode || isChatMode ? 'Upload media for your template' : 'Upload once per send (media IDs expire after ~30 days).'}
            </p>
          </div>
        </div>
        <Badge variant="secondary" className="bg-gray-50 dark:bg-slate-950 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 capitalize text-[10px]">
          {headerType}
        </Badge>
      </div>

      {/* Upload Area */}
      <div className="px-4 py-4">
        <Label className="text-gray-700 dark:text-gray-300 text-sm">
          Media {isCreateMode || isChatMode ? 'file' : 'to send'} <span className="text-red-500">*</span> {!(isCreateMode || isChatMode) && '(one file for everyone)'}
        </Label>

        {error && (
          <div className="mt-2 p-3 rounded-md bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800">
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}

        {hasFile ? (
          <div className="mt-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-slate-950 px-4 py-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                ✓ {displayFileName}
              </p>
              {isCreateMode || isChatMode ? (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {displayFileSize ? `${(displayFileSize / 1024).toFixed(2)} KB` : ''} - {config?.maxSizeText}
                </p>
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  We re-upload for every send batch to stay compliant.
                </p>
              )}
            </div>
            <button
              onClick={handleRemoveFile}
              disabled={disabled}
              className="shrink-0 p-2 rounded-md hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <X className="h-4 w-4 text-gray-600 dark:text-gray-400" />
            </button>
          </div>
        ) : (
          <div className="mt-2 rounded-lg border border-dashed border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-slate-950 px-4 py-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                No file chosen
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {isCreateMode || isChatMode ? config?.maxSizeText : 'We re-upload for every send batch to stay compliant.'}
              </p>
            </div>
            <label className={`shrink-0 ${!disabled ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept={getAcceptedFormats()}
                onChange={handleFileChange}
                disabled={disabled}
              />
              <span className={`px-3 py-2 rounded-md bg-white dark:bg-slate-900 border border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors inline-block ${!disabled ? 'hover:bg-gray-50 dark:hover:bg-slate-800 cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}>
                Choose file
              </span>
            </label>
          </div>
        )}
      </div>
    </div>
  );
};

export default SendTemplateMediaUploader;
